import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useRobots } from "@/components/seo/Seo";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftRight, Baby, BadgeCheck, Check, Clock, Headset, Lock, MapPin, Minus, Plane, Plus, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import StatusLine from "@/components/StatusLine";
import VehicleCard from "@/components/VehicleCard";
import BookingActions from "@/components/booking/BookingActions";
import DurationSelect from "@/components/booking/DurationSelect";
import BookingHeader from "@/components/booking/BookingHeader";
import PassengerSelector from "@/components/booking/PassengerSelector";
import SumUpPayment from "@/components/booking/SumUpPayment";
import TripSummary, { MobileTripSummary } from "@/components/booking/TripSummary";
import VehicleActionPanel from "@/components/booking/VehicleActionPanel";
import VehicleDetailPanel from "@/components/booking/VehicleDetailPanel";
import { DateField, FieldLabel, TimeField, inputCls } from "@/components/booking/BookingFields";
import { VEHICLES, formatChf } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
// attribution guide → réservation (src=guide:<id> posé par le CTA des Guides MILES)
const guideSrc = () => { try { return sessionStorage.getItem("miles-guide-src") || null; } catch { return null; } };
const TRIP_TYPES = ["one_way", "round_trip", "hourly"];
const stepMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.22, ease: "easeOut" },
};

export default function Booking() {
  const { t, lang } = useLang();
  useRobots("noindex, follow");
  const [params] = useSearchParams();
  useEffect(() => {
    const src = params.get("src") || "";
    if (src.startsWith("guide:")) sessionStorage.setItem("miles-guide-src", src.slice(6));
    trackEvent("booking_started", { route_id: params.get("from") ? `${params.get("from")}|${params.get("to") || ""}` : null, guide_id: guideSrc() });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [step, setStep] = useState(0);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateTick, setEstimateTick] = useState(0);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);
  const [showRequests, setShowRequests] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [form, setForm] = useState({
    trip_type: TRIP_TYPES.includes(params.get("type")) ? params.get("type") : "one_way",
    booking_for: "self",
    pickup: params.get("from") || "",
    dropoff: params.get("to") || "",
    pickup_place: (params.get("from_lat") && params.get("from_lon"))
      ? { lat: Number(params.get("from_lat")), lon: Number(params.get("from_lon")) } : null,
    dropoff_place: (params.get("to_lat") && params.get("to_lon"))
      ? { lat: Number(params.get("to_lat")), lon: Number(params.get("to_lon")) } : null,
    date: params.get("date") || today,
    time: params.get("time") || "09:00",
    return_date: params.get("return_date") || tomorrow,
    return_time: params.get("return_time") || "17:30",
    duration_hours: 4,
    stops: [],
    stops_on_return: false,
    passengers: 1,
    luggage: 1,
    flight_number: "",
    terminal: "",
    notes: "",
    options: [],
    terms_accepted: false,
    terms_accepted_at: null,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    vehicle_category: VEHICLES[params.get("vehicle")] ? params.get("vehicle") : null,
    vehicles: VEHICLES[params.get("vehicle")] ? [{ category: params.get("vehicle"), count: 1 }] : [],
    price_chf: null,
  });
  const [payment, setPayment] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  // moyen de paiement particulier : "sumup" (en ligne) | "card_onboard" | "cash"
  const [payMethod, setPayMethod] = useState("sumup");
  // après un refus/échec, on force la création d'un NOUVEAU checkout SumUp
  // (un checkout FAILED est définitivement mort côté SumUp — le réutiliser affiche « Échoué » en boucle)
  const [paymentEpoch, setPaymentEpoch] = useState(0);
  const resetPayment = useCallback(() => {
    setPayment(null);
    setCardComplete(false);
    sessionStorage.removeItem("miles_payment_tracking");
    setPaymentEpoch((n) => n + 1);
  }, []);
  const [promoInput, setPromoInput] = useState("");
  const [promoCode, setPromoCode] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const payFnRef = useRef(null);
  const registerPay = useRef((fn) => { payFnRef.current = fn; }).current;

  const set = (key) => (valueOrEvent) => setForm((current) => ({
    ...current,
    [key]: valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent,
  }));

  const toggleOption = (option) => setForm((current) => ({
    ...current,
    options: current.options.includes(option)
      ? current.options.filter((item) => item !== option)
      : [...current.options, option],
  }));

  // multi-véhicules : sélection par catégorie avec quantités (comme Miles Business)
  const toggleVehicle = (categoryId) => setForm((current) => {
    const exists = (current.vehicles || []).find((v) => v.category === categoryId);
    const vehicles = exists
      ? current.vehicles.filter((v) => v.category !== categoryId)
      : [...(current.vehicles || []), { category: categoryId, count: 1 }];
    return { ...current, vehicles, vehicle_category: vehicles[0]?.category || null };
  });
  const changeVehicleCount = (categoryId, delta) => setForm((current) => ({
    ...current,
    vehicles: (current.vehicles || []).map((v) => (v.category === categoryId
      ? { ...v, count: Math.min(8, Math.max(1, v.count + delta)) } : v)),
  }));
  // total groupe = Σ prix unitaire × quantité (le serveur recalcule — jamais fiable côté client)
  const vehiclesTotal = (form.vehicles || []).reduce((sum, v) => {
    const c = estimate?.categories.find((cat) => cat.id === v.category);
    return sum + (c ? c.price_chf * v.count : 0);
  }, 0);
  useEffect(() => {
    setForm((current) => (current.price_chf !== (vehiclesTotal || null)
      ? { ...current, price_chf: vehiclesTotal || null } : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclesTotal]);


  const airportTrip = /aéroport|airport|gva|zrh|bsl/i.test(`${form.pickup} ${form.dropoff}`);
  const routeReady = form.trip_type === "hourly"
    ? form.pickup.trim().length > 2
    : form.pickup.trim().length > 2 && form.dropoff.trim().length > 2 && form.pickup.trim().toLowerCase() !== form.dropoff.trim().toLowerCase();

  const activeStops = form.trip_type !== "hourly"
    ? form.stops
        .filter((s) => (s.address || "").trim().length > 2)
        .map((s) => ({ address: s.address, place: s.place, wait_minutes: Number(s.wait_minutes) || 0 }))
    : [];
  const stopsOnReturn = form.trip_type === "round_trip" ? form.stops_on_return : false;

  useEffect(() => {

    if (!routeReady) {
      setEstimate(null);
      setEstimating(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setEstimating(true);
      axios.post(`${API}/estimate`, {
        pickup: form.pickup,
        dropoff: form.trip_type === "hourly" ? null : form.dropoff,
        pickup_place: form.pickup_place,
        dropoff_place: form.trip_type === "hourly" ? null : form.dropoff_place,
        trip_type: form.trip_type,
        duration_hours: form.duration_hours,
        stops: activeStops,
        stops_on_return: stopsOnReturn,
      }).then((response) => {
        if (cancelled) return;
        setEstimate(response.data);
      }).catch(() => {
        if (cancelled) return;
        setEstimate(null);
      }).finally(() => {
        if (!cancelled) setEstimating(false);
      });
    }, 550);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [routeReady, form.pickup, form.dropoff, form.pickup_place, form.dropoff_place, form.trip_type, form.duration_hours, JSON.stringify(form.stops), form.stops_on_return, estimateTick]);

  const bookingPayload = () => ({
    trip_type: form.trip_type,
    booking_for: form.booking_for,
    pickup: form.pickup,
    dropoff: form.trip_type === "hourly" ? null : form.dropoff,
    pickup_place: form.pickup_place,
    dropoff_place: form.trip_type === "hourly" ? null : form.dropoff_place,
    date: form.date,
    time: form.time,
    return_date: form.trip_type === "round_trip" ? form.return_date : null,
    return_time: form.trip_type === "round_trip" ? form.return_time : null,
    duration_hours: form.trip_type === "hourly" ? form.duration_hours : null,
    stops: activeStops,
    stops_on_return: stopsOnReturn,
    passengers: form.passengers,
    luggage: form.luggage,
    flight_number: form.flight_number || null,
    terminal: form.terminal || null,
    notes: form.notes || null,
    options: form.options,
    terms_accepted: form.terms_accepted,
    terms_accepted_at: form.terms_accepted ? new Date().toISOString() : null,
    lang,
    vehicle_category: form.vehicles?.[0]?.category || null,
    vehicles: form.vehicles?.length ? form.vehicles : null,
    price_chf: vehiclesTotal || form.price_chf,
    customer: {
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
    },
  });

  const paymentSignature = JSON.stringify([
    form.trip_type, form.booking_for, form.pickup, form.dropoff, form.date, form.time,
    form.return_date, form.return_time, form.duration_hours, form.passengers, form.luggage,
    form.stops, form.stops_on_return,
    form.flight_number, form.terminal, form.notes, form.options,
    form.vehicles, form.price_chf, form.first_name, form.last_name, form.email, form.phone,
    promoCode,
  ]);

  const valid = useMemo(() => [
    routeReady && Boolean(form.date && form.time) && (form.trip_type !== "round_trip" || Boolean(form.return_date && form.return_time)),
    Boolean(form.vehicles?.length && estimate && !estimating),
    Boolean(form.first_name.trim() && form.last_name.trim() && /.+@.+\..+/.test(form.email) && form.phone.trim().length >= 6),
    Boolean(form.terms_accepted && (payMethod !== "sumup" || (cardComplete && payment?.checkout_id && !intentLoading))),
  ], [routeReady, form, estimate, estimating, cardComplete, payment, intentLoading, payMethod]);

  const detailsValid = valid[0] && valid[1] && valid[2];

  useEffect(() => {
    if (step !== 3 || !detailsValid || payMethod !== "sumup") return undefined;
    let cancelled = false;
    setIntentLoading(true);
    axios.post(`${API}/payments/sumup/create-checkout`, {
      booking: bookingPayload(),
      tracking_code: payment?.tracking_code || sessionStorage.getItem("miles_payment_tracking") || null,
      promo_code: promoCode || null,
      origin_url: window.location.origin,
    }).then((response) => {
      if (cancelled) return;
      sessionStorage.setItem("miles_payment_tracking", response.data.tracking_code);
      setPayment((previous) => {
        if (previous?.checkout_id !== response.data.checkout_id) setCardComplete(false);
        return response.data;
      });
      setForm((current) => ({ ...current, price_chf: response.data.amount_chf }));
    }).catch((error) => {
      if (cancelled) return;
      setPayment(null);
      if (error?.response?.status === 422 && promoCode) {
        setPromoCode(null);
        setPromoError(t("bk.promo_invalid"));
      } else {
        toast.error(t("bk.pay_err_t"), { description: t("bk.pay_err_d") });
      }
    }).finally(() => {
      if (!cancelled) setIntentLoading(false);
    });
    return () => { cancelled = true; };
  }, [step, paymentSignature, paymentEpoch]);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code || promoChecking) return;
    setPromoChecking(true);
    setPromoError(null);
    try {
      const response = await axios.post(`${API}/promo/validate`, { code });
      setPromoCode(response.data.code);
      setPromoInput("");
    } catch {
      setPromoError(t("bk.promo_invalid"));
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => { setPromoCode(null); setPromoError(null); };

  const swapRoute = () => setForm((current) => ({
    ...current,
    pickup: current.dropoff,
    dropoff: current.trip_type === "hourly" ? "" : current.pickup,
    pickup_place: current.dropoff_place,
    dropoff_place: current.pickup_place,
  }));

  const addStop = () => setForm((c) => ({ ...c, stops: [...c.stops, { address: "", place: null, wait_minutes: 0 }] }));
  const removeStop = (i) => setForm((c) => ({ ...c, stops: c.stops.filter((_, j) => j !== i) }));
  const updateStop = (i, patch) => setForm((c) => ({ ...c, stops: c.stops.map((s, j) => j === i ? { ...s, ...patch } : s) }));

  const pay = async () => {
    if (!valid[3]) return;
    setPaying(true);
    try {
      // règlement à bord (carte ou espèces) : réservation confirmée sans paiement en ligne
      if (payMethod !== "sumup") {
        const onboard = await axios.post(`${API}/payments/book-onboard`, {
          booking: bookingPayload(),
          payment_method: payMethod,
          promo_code: promoCode || null,
          terms_accepted: true,
          terms_accepted_at: new Date().toISOString(),
          origin_url: window.location.origin,
        });
        sessionStorage.removeItem("miles_payment_tracking");
        setResult(onboard.data);
        trackEvent("booking_completed", { amount: onboard.data?.price_chf ?? null, payment_method: payMethod, guide_id: guideSrc() });
        setStep(4);
        window.scrollTo({ top: 0, behavior: "auto" });
        toast.success(t("bk.booked_t"), { description: t("bk.booked_b") });
        return;
      }
      if (!payFnRef.current) return;
      const confirmation = await payFnRef.current();
      if (confirmation?.error) {
        toast.error(t("bk.pay_err_t"), {
          description: confirmation.error.message === "not_ready" ? t("bk.pay_err_d") : confirmation.error.message,
        });
        resetPayment(); // checkout mort → nouvelle tentative = nouveau checkout
        return;
      }
      // 3DS / latence SumUp : le checkout peut passer PAID quelques secondes après
      // le retour du widget — vérification serveur en boucle (25 × 3 s max).
      let response = null;
      for (let attempt = 0; attempt < 25; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          response = await axios.post(`${API}/payments/sumup/confirm`, {
            tracking_code: payment.tracking_code,
            checkout_id: payment.checkout_id,
            terms_accepted: form.terms_accepted,
            terms_accepted_at: form.terms_accepted ? new Date().toISOString() : null,
            origin_url: window.location.origin,
          });
          break;
        } catch (error) {
          const detail = error?.response?.data?.detail || "";
          if (error?.response?.status === 402 && detail.includes("failed")) throw error; // refus confirmé → stop
          if (error?.response?.status !== 402 || attempt === 24) throw error;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => { setTimeout(r, 3000); });
        }
      }
      sessionStorage.removeItem("miles_payment_tracking");
      setResult(response.data);
      trackEvent(response.data?.payment_status === "paid" ? "payment_completed" : "booking_completed",
        { amount: response.data?.price_chf ?? null, guide_id: guideSrc() });
      setStep(4);
      window.scrollTo({ top: 0, behavior: "auto" });
      toast.success(t("bk.paid_t"), { description: t("bk.paid_b") });
    } catch (error) {
      const detail = error?.response?.data?.detail || "";
      const declined = error?.response?.status === 402 && detail.includes("failed");
      const pending = error?.response?.status === 402;
      if (pending && !declined) {
        // SumUp n'a pas encore tranché (3DS lent) — le webhook confirmera : on informe, on ne bloque pas
        toast(t("bk.pay_pending_t"), {
          description: t("bk.pay_pending_b").replace("{code}", payment?.tracking_code || ""),
        });
      } else {
        toast.error(t("bk.pay_err_t"), { description: declined ? t("bk.pay_declined") : t("bk.pay_err_d") });
        if (declined) resetPayment(); // refus confirmé → prochain essai = checkout frais
      }
    } finally {
      setPaying(false);
    }
  };

  const selectedCategory = estimate?.categories.find((category) => category.id === form.vehicle_category) || null;
  const previewCategory = selectedCategory || estimate?.categories[0] || null;
  const previewVehicle = previewCategory ? { ...VEHICLES[previewCategory.id], id: previewCategory.id, pax: previewCategory.pax, bags: previewCategory.bags } : null;


  const tripTypes = (
    <div className="grid grid-cols-3 gap-1 rounded-2xl bg-cloud p-1" data-testid="booking-trip-types">
      {TRIP_TYPES.map((type) => (
        <button key={type} type="button" onClick={() => setForm((current) => ({ ...current, trip_type: type }))}
          data-testid={`booking-type-${type}`} aria-pressed={form.trip_type === type}
          className={`rounded-xl px-3 py-3 text-sm font-medium transition-colors ${form.trip_type === type ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink"}`}>
          {t(`bk.types.${type}`)}
        </button>
      ))}
    </div>
  );

  const routeStep = (
    <section className="max-w-3xl">
      <h1 className="text-4xl font-semibold tracking-[-0.045em] text-ink sm:text-5xl" data-testid="booking-title">{t("bk.questions")[0]}</h1>
      <p className="mt-3 text-base leading-relaxed text-slate">{t("bk.route_step_sub")}</p>
      <div className="mt-8">{tripTypes}</div>

      <div className="mt-6 rounded-[1.5rem] border border-ink/10 bg-white p-5 shadow-[0_18px_55px_-42px_rgba(16,24,40,0.28)] sm:p-6" data-testid="trip-form">
        <div className={`relative grid gap-4 ${form.trip_type === "hourly" ? "" : "lg:grid-cols-2"}`}>
          <AddressAutocomplete label={form.trip_type === "hourly" ? t("bk.from_hourly") : t("bk.from")} icon={MapPin} value={form.pickup} onChange={set("pickup")}
            onSelect={(place) => setForm((current) => ({ ...current, pickup_place: place }))}
            bias={form.dropoff_place || undefined}
            testId="booking-pickup-select" placeholder={t("bk.from_ph")} />
          {form.trip_type !== "hourly" && (
            <AddressAutocomplete label={t("bk.to")} icon={MapPin} value={form.dropoff} onChange={set("dropoff")}
              onSelect={(place) => setForm((current) => ({ ...current, dropoff_place: place }))}
              bias={form.pickup_place || undefined}
              testId="booking-dropoff-select" placeholder={t("bk.to_ph")} />
          )}
          {form.trip_type !== "hourly" && (
            <button type="button" onClick={swapRoute} data-testid="booking-swap-route" aria-label={t("bk.swap_route")}
              className="absolute left-1/2 top-1/2 hidden h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-ink/10 bg-white text-slate shadow-sm transition-colors hover:border-miles hover:text-miles lg:flex">
              <ArrowLeftRight className="h-4 w-4 rotate-90" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {form.trip_type !== "hourly" && (
          <div className="mt-4 space-y-3" data-testid="booking-stops">
            {form.stops.map((stop, i) => (
              <div key={i} className="rounded-2xl border border-ink/10 bg-cloud/40 p-3" data-testid={`booking-stop-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">Arrêt {i + 1}</span>
                  <button type="button" onClick={() => removeStop(i)} data-testid={`booking-stop-remove-${i}`}
                    className="inline-flex items-center gap-1 text-xs text-slate transition-colors hover:text-red-600">
                    <X className="h-3.5 w-3.5" strokeWidth={2} /> Retirer
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
                  <AddressAutocomplete label={`Adresse de l'arrêt`} icon={MapPin} value={stop.address}
                    onChange={(v) => updateStop(i, { address: v?.target ? v.target.value : v })}
                    onSelect={(place) => updateStop(i, { place })}
                    bias={form.pickup_place || form.dropoff_place || undefined}
                    testId={`booking-stop-address-${i}`} placeholder="Ville, adresse…" />
                  <div>
                    <FieldLabel icon={Clock}>Attente (min)</FieldLabel>
                    <input type="number" min="0" step="5" inputMode="numeric" value={stop.wait_minutes}
                      onChange={(e) => updateStop(i, { wait_minutes: Math.max(0, Number(e.target.value) || 0) })}
                      data-testid={`booking-stop-wait-${i}`} className={inputCls} placeholder="0" />
                  </div>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button type="button" onClick={addStop} data-testid="booking-add-stop"
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-miles/50 px-4 py-2 text-sm font-semibold text-miles transition-colors hover:bg-miles/5">
                <Plus className="h-4 w-4" strokeWidth={2} /> Ajouter un arrêt
              </button>
              {form.stops.length > 0 && (
                <span className="text-xs text-slate">Attente facturée 90.-/h dès 15 min · 0-14 min offert</span>
              )}
            </div>
            {form.trip_type === "round_trip" && form.stops.length > 0 && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={form.stops_on_return}
                  onChange={(e) => setForm((c) => ({ ...c, stops_on_return: e.target.checked }))}
                  data-testid="booking-stops-on-return"
                  className="h-4 w-4 rounded border-ink/20 text-miles focus:ring-miles" />
                Effectuer aussi ces arrêts au retour
              </label>
            )}
          </div>
        )}

        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_0.65fr_1fr]">
          <DateField label={t("bk.date")} value={form.date} min={today} onChange={set("date")} testId="booking-date-input" />
          <TimeField label={t("bk.time")} value={form.time} onChange={set("time")} testId="booking-time-input" />
          <div>
            <FieldLabel>{t("bk.pax")}</FieldLabel>
            <PassengerSelector passengers={form.passengers} luggage={form.luggage}
              onPassengers={set("passengers")} onLuggage={set("luggage")} />
          </div>
        </div>

        {form.trip_type === "hourly" && (
          <div className="mt-5">
            <DurationSelect value={form.duration_hours} onChange={(hours) => setForm((current) => ({ ...current, duration_hours: hours }))} t={t} />
          </div>
        )}

        {form.trip_type === "round_trip" && (
          <div className="mt-5 rounded-2xl bg-cloud p-4" data-testid="return-fields">
            <p className="mb-3 text-sm font-medium text-ink">{t("bk.return_t")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <DateField label={t("bk.date")} value={form.return_date} min={form.date} onChange={set("return_date")} testId="booking-return-date-input" />
              <TimeField label={t("bk.time")} value={form.return_time} onChange={set("return_time")} testId="booking-return-time-input" />
            </div>
          </div>
        )}

        {airportTrip && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
            className="mt-5 rounded-2xl border border-miles/15 bg-miles-ice/55 p-4" data-testid="airport-fields">
            <p className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Plane className="h-4 w-4 text-miles" strokeWidth={1.5} /> {t("bk.flight_context")}
            </p>
            <p className="mt-1 text-sm text-slate">{t("bk.flight_context_sub")}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input value={form.flight_number} onChange={set("flight_number")} placeholder={t("bk.flight_ph")} data-testid="booking-flight-input" className={inputCls} />
              <input value={form.terminal} onChange={set("terminal")} placeholder={t("bk.terminal_ph")} data-testid="booking-terminal-input" className={inputCls} />
            </div>
          </motion.div>
        )}
      </div>

      <div className="mt-5 grid gap-3 rounded-[1.25rem] border border-ink/10 bg-white/70 p-4 sm:grid-cols-3" data-testid="booking-trust-signals">
        {t("bk.trust_items").map((item, index) => {
          const Icon = [ShieldCheck, BadgeCheck, Headset][index] || Check;
          return (
            <p key={item} className="flex items-start gap-2.5 text-sm leading-snug text-slate">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-miles" strokeWidth={1.5} /> {item}
            </p>
          );
        })}
      </div>
    </section>
  );

  const vehicleStep = (
    <section className="max-w-4xl">
      <h1 className="text-4xl font-semibold tracking-[-0.045em] text-ink sm:text-5xl" data-testid="booking-title">{t("bk.questions")[1]}</h1>
      <p className="mt-3 text-base text-slate">{t("bk.vehicle_step_sub")}</p>
      <p className="mt-1.5 text-sm font-medium text-miles" data-testid="vehicle-multi-hint">{t("bk.multi_hint")}</p>
      {estimating && (
        <div className="mt-8 space-y-4" data-testid="estimate-loading">
          {[0, 1, 2].map((item) => <div key={item} className="h-52 animate-pulse rounded-[1.5rem] border border-ink/10 bg-white/75" />)}
        </div>
      )}
      {!estimating && !estimate && (
        <div className="mt-8 rounded-[1.5rem] border border-dashed border-ink/15 bg-white p-8" data-testid="estimate-error">
          <p className="text-base font-semibold text-ink">{t("bk.est_unavailable")}</p>
          <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate">{t("bk.est_unavailable_hint")}</p>
          <button type="button" onClick={() => setEstimateTick((value) => value + 1)} data-testid="estimate-retry-button"
            className="mt-5 rounded-full bg-miles px-5 py-3 text-sm font-semibold text-white">
            {t("bk.retry_estimate")}
          </button>
        </div>
      )}
      {!estimating && estimate && (
        <div className="mt-8 space-y-5" data-testid="vehicle-options">
          <div className="grid gap-4 md:grid-cols-3">
            {estimate.categories.map((category) => {
              const qty = (form.vehicles || []).find((v) => v.category === category.id)?.count || 0;
              return (
                <div key={category.id} className="relative">
                  <VehicleCard vehicle={{ ...VEHICLES[category.id], id: category.id, pax: category.pax, bags: category.bags }}
                    price={category.price_chf}
                    note={form.trip_type === "hourly" && category.included_km
                      ? t("bk.hourly_km_note").replace("{km}", category.included_km).replace("{rate}", formatChf(category.extra_km_rate_chf))
                      : null}
                    selected={qty > 0}
                    onSelect={() => toggleVehicle(category.id)}
                    testId={`vehicle-option-${category.id}`} />
                  {qty > 0 && (
                    <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-full border border-ink/10 bg-white px-1 py-1 shadow-lg" data-testid={`vehicle-qty-${category.id}`}>
                      <button type="button" aria-label="-" disabled={qty <= 1} data-testid={`vehicle-qty-minus-${category.id}`}
                        onClick={() => changeVehicleCount(category.id, -1)}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-ink transition-colors hover:bg-cloud disabled:opacity-30">
                        <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                      <span className="min-w-5 text-center text-sm font-bold text-ink" data-testid={`vehicle-qty-value-${category.id}`}>{qty}</span>
                      <button type="button" aria-label="+" disabled={qty >= 8} data-testid={`vehicle-qty-plus-${category.id}`}
                        onClick={() => changeVehicleCount(category.id, 1)}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-ink transition-colors hover:bg-cloud disabled:opacity-30">
                        <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {previewVehicle && previewCategory && (
            <VehicleDetailPanel vehicle={previewVehicle} price={previewCategory.price_chf}
              minutes={form.trip_type === "hourly" ? null : estimate.minutes} />
          )}
        </div>
      )}
    </section>
  );

  const travelerStep = (
    <section className="max-w-2xl">
      <h1 className="text-4xl font-semibold tracking-[-0.045em] text-ink sm:text-5xl" data-testid="booking-title">{t("bk.questions")[2]}</h1>
      <div className="mt-8 grid grid-cols-2 gap-1 rounded-2xl bg-cloud p-1" data-testid="booking-for-selector">
        {["self", "guest"].map((id) => (
          <button key={id} type="button" onClick={() => setForm((current) => ({ ...current, booking_for: id }))}
            data-testid={`booking-for-${id}`} aria-pressed={form.booking_for === id}
            className={`rounded-xl px-4 py-3 text-sm font-medium transition-colors ${form.booking_for === id ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink"}`}>
            {id === "self" ? t("bk.for_me") : t("bk.for_guest")}
          </button>
        ))}
      </div>
      {form.booking_for === "guest" && <p className="mt-3 text-sm text-slate" data-testid="booking-guest-note">{t("bk.guest_note")}</p>}

      <div className="mt-6 rounded-[1.5rem] border border-ink/10 bg-white p-5 sm:p-6" data-testid="traveller-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <input value={form.first_name} onChange={set("first_name")} placeholder={t("bk.fname")} aria-label={t("bk.fname")} data-testid="customer-firstname-input" className={inputCls} />
          <input value={form.last_name} onChange={set("last_name")} placeholder={t("bk.lname")} aria-label={t("bk.lname")} data-testid="customer-lastname-input" className={inputCls} />
        </div>
        <input type="email" value={form.email} onChange={set("email")} placeholder={t("bk.email")} data-testid="customer-email-input" className={`${inputCls} mt-4`} />
        <input type="tel" value={form.phone} onChange={set("phone")} placeholder={t("bk.phone_ph")} data-testid="customer-phone-input" className={`${inputCls} mt-4`} />
        {!showRequests ? (
          <button type="button" onClick={() => setShowRequests(true)} data-testid="booking-add-request"
            className="mt-4 flex items-center gap-2 text-sm font-medium text-miles transition-colors hover:text-miles-dark">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> {t("bk.add_request")}
          </button>
        ) : (
          <textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="booking-notes-input"
            placeholder={t("bk.notes_ph")} className={`${inputCls} mt-4 h-auto resize-none py-4`} />
        )}

        <div className="mt-5 border-t border-ink/10 pt-5" data-testid="booking-options">
          <p className="text-sm font-semibold text-ink">{t("bk.options_title")}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {["child_seat", "meet_greet"].map((option) => {
              const active = form.options.includes(option);
              const Icon = option === "child_seat" ? Baby : BadgeCheck;
              return (
                <button key={option} type="button" onClick={() => toggleOption(option)} aria-pressed={active} data-testid={`booking-option-${option.replace("_", "-")}`}
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-medium transition-colors ${active ? "border-miles bg-miles-ice/60 text-ink" : "border-ink/10 bg-white text-slate hover:border-miles/30 hover:text-ink"}`}>
                  <Icon className={`h-4 w-4 ${active ? "text-miles" : "text-slate"}`} strokeWidth={1.5} />
                  {t(`bk.option_${option}`)}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs leading-relaxed text-slate">{t("bk.options_note")}</p>
        </div>
      </div>
    </section>
  );

  const paymentStep = (
    <section className="max-w-2xl">
      <h1 className="text-4xl font-semibold tracking-[-0.045em] text-ink sm:text-5xl" data-testid="booking-title">{t("bk.questions")[3]}</h1>
      <p className="mt-3 text-base text-slate">{t("bk.payment_sub")}</p>

      <div className="mt-8" data-testid="promo-block">
        <p className="text-sm font-semibold text-ink">{t("bk.promo_label")}</p>
        {promoCode ? (
          <div className="mt-3 flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="promo-applied">
            <span className="flex items-center gap-2 text-sm font-medium text-emerald-700">
              <BadgeCheck className="h-4 w-4" strokeWidth={1.5} />
              {promoCode}{payment?.promo ? ` · −${payment.promo.percent_off} %` : ""}
            </span>
            <button type="button" onClick={removePromo} data-testid="promo-remove-button"
              className="text-xs font-medium text-slate transition-colors hover:text-ink">
              {t("bk.promo_remove")}
            </button>
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <input value={promoInput} onChange={(e) => { setPromoInput(e.target.value); setPromoError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
              placeholder={t("bk.promo_ph")} data-testid="promo-code-input"
              className={`${inputCls} uppercase`} />
            <button type="button" onClick={applyPromo} disabled={!promoInput.trim() || promoChecking} data-testid="promo-apply-button"
              className="shrink-0 rounded-2xl bg-ink px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40">
              {promoChecking ? "…" : t("bk.promo_apply")}
            </button>
          </div>
        )}
        {promoError && <p className="mt-2 text-xs text-red-600" data-testid="promo-error">{promoError}</p>}
      </div>

      <div className="mt-5 rounded-[1.5rem] border border-ink/10 bg-white p-5 sm:p-6" data-testid="payment-card">
        <div className="flex items-center gap-2 text-sm font-medium text-ink">
          <Lock className="h-4 w-4 text-miles" strokeWidth={1.5} /> {t("bk.pay_t")}
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="payment-method-selector">
          {[
            ["sumup", t("bk.pay_online"), t("bk.pay_online_h")],
            ["card_onboard", t("bk.pay_card_board"), t("bk.pay_card_board_h")],
            ["cash", t("bk.pay_cash"), t("bk.pay_cash_h")],
          ].map(([value, label, hint]) => (
            <button key={value} type="button" onClick={() => setPayMethod(value)} data-testid={`pay-method-${value}`}
              aria-pressed={payMethod === value}
              className={`rounded-2xl border p-4 text-left transition-[border-color,background-color] ${payMethod === value ? "border-miles bg-miles/[0.06]" : "border-ink/10 bg-white hover:border-ink/25"}`}>
              <p className="text-sm font-semibold text-ink">{label}</p>
              <p className="mt-1 text-xs leading-snug text-slate">{hint}</p>
            </button>
          ))}
        </div>
        {payMethod === "sumup" ? (
          <div className="mt-5">
            <SumUpPayment checkoutId={payment?.checkout_id || null}
              registerPay={registerPay} onCompleteChange={setCardComplete}
              email={form.email} />
          </div>
        ) : (
          <p className="mt-5 rounded-2xl bg-cloud px-4 py-4 text-sm leading-relaxed text-slate" data-testid="pay-onboard-note">
            {t(payMethod === "cash" ? "bk.pay_cash_note" : "bk.pay_card_note")}
          </p>
        )}
        <p className="mt-5 text-sm leading-relaxed text-slate">{t("bk.secure")}</p>
        {payMethod === "sumup" && (
          <p className="mt-2 text-xs leading-relaxed text-slate" data-testid="payment-methods-note">{t("bk.pay_methods_note")}</p>
        )}
      </div>

        <div className="mt-5 rounded-2xl bg-cloud p-4" data-testid="booking-terms">
          <label className="flex items-start gap-3 text-sm leading-relaxed text-ink">
            <input type="checkbox" checked={form.terms_accepted} data-testid="booking-terms-checkbox"
              onChange={(event) => setForm((current) => ({ ...current, terms_accepted: event.target.checked }))}
              className="mt-0.5 h-5 w-5 rounded border-ink/20 accent-miles" />
            <span>{t("bk.terms_text")}</span>
          </label>
          <button type="button" onClick={() => setShowTerms(!showTerms)} aria-expanded={showTerms} data-testid="booking-terms-toggle"
            className="mt-3 text-sm font-medium text-miles transition-colors hover:text-miles-dark">
            {showTerms ? t("bk.terms_hide") : t("bk.terms_view")}
          </button>
          {showTerms && (
            <ul className="mt-3 space-y-2 border-t border-ink/10 pt-3 text-sm leading-relaxed text-slate" data-testid="booking-terms-list">
              {t("bk.terms_items").map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-miles" strokeWidth={1.5} /> {item}
                </li>
              ))}
            </ul>
          )}
        </div>
    </section>
  );

  const confirmation = result && (
    <section className="mx-auto max-w-2xl text-center" data-testid="booking-confirmation">
      <motion.div initial={{ scale: 0.88, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-miles text-white shadow-[0_18px_45px_rgba(36,87,255,0.26)]">
        <Check className="h-7 w-7" strokeWidth={2.5} />
      </motion.div>
      <h1 className="mt-7 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl">{t("bk.confirm_title")}</h1>
      <p className="mt-3 text-base text-slate">{t("bk.done_sub")}</p>
      <div className="mt-8 rounded-[1.5rem] border border-ink/10 bg-white p-6 text-left shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            [t("bk.l_ride"), `${result.pickup} → ${result.dropoff || t("bk.hourly_word")}`],
            [t("bk.l_dt"), `${result.date} · ${result.time}`],
            [t("bk.l_vehicle"), result.units?.length > 1
              ? `${result.units.length} ${t("bk.vehicles_word")}`
              : `${t(`veh.${result.vehicle_category}.name`)} — ${t(`veh.${result.vehicle_category}.model`)}`],
            [t("bk.l_amount"), formatChf(result.price_chf)],
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl bg-cloud p-4">
              <p className="text-xs text-slate">{label}</p>
              <p className="mt-1 text-sm font-semibold text-ink">{value}</p>
            </div>
          ))}
        </div>
        <p className="mt-5 font-mono text-sm font-semibold text-ink" data-testid="tracking-code">{result.tracking_code}</p>
        <div className="mt-5"><StatusLine current="booked" testId="confirmation-status-line" /></div>
      </div>
      {result.units?.length > 1 && (
        <div className="mx-auto mt-7 w-full max-w-md" data-testid="confirmation-units">
          <p className="mb-2.5 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t("bk.units_title")}</p>
          <div className="space-y-2">
            {result.units.map((u) => (
              <Link key={u.tracking_code} to={`/suivi/${u.tracking_code}`} data-testid={`tracking-link-unit-${u.unit_index}`}
                className="flex items-center justify-between rounded-2xl border border-ink/10 bg-white px-4 py-3 text-sm transition-colors duration-200 hover:border-miles">
                <span className="font-semibold text-ink">{t(`veh.${u.vehicle_category}.name`)} <span className="text-slate">· {u.unit_index}/{u.unit_total}</span></span>
                <span className="font-mono text-xs font-semibold text-miles">{u.tracking_code} →</span>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
        {!(result.units?.length > 1) && (
          <Link to={`/suivi/${result.tracking_code}`} data-testid="tracking-link"
            className="rounded-full bg-miles px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-miles-dark">
            {t("bk.track_cta")}
          </Link>
        )}
        <Link to="/" data-testid="booking-home-link"
          className="rounded-full border border-ink/10 bg-white px-7 py-3.5 text-sm font-semibold text-ink">
          {t("bk.view_booking")}
        </Link>
      </div>
    </section>
  );

  const stepContent = [routeStep, vehicleStep, travelerStep, paymentStep];

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#FFFFFF_0%,#F6F8FC_52%,#EAF4FF_100%)] font-sans text-ink" data-testid="booking-page">
      <BookingHeader step={Math.min(step, 3)} />
      {step < 4 && <MobileTripSummary form={form} estimate={estimate} />}
      <main className="mx-auto max-w-[1360px] px-5 pb-32 pt-8 sm:px-8 lg:pt-12">
        {step < 4 ? (
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <AnimatePresence mode="wait">
                <motion.div key={step} {...stepMotion}>{stepContent[step]}</motion.div>
              </AnimatePresence>
              <BookingActions step={step} valid={valid[step]} paying={paying} price={form.price_chf} onboard={payMethod !== "sumup"} desktopCta={step !== 1}
                onBack={() => setStep(step - 1)} onNext={() => valid[step] && setStep(step + 1)} onPay={pay} />
            </div>
            <div className="lg:col-span-4">
              <TripSummary form={form} estimate={estimate} onEdit={setStep} hidePrice={step === 1} promo={payment?.promo || null} sticky={step !== 1} />
              {step === 1 && selectedCategory && (
                <VehicleActionPanel
                  vehicle={{ ...VEHICLES[selectedCategory.id], id: selectedCategory.id }}
                  price={form.price_chf ?? selectedCategory.price_chf}
                  unitsCount={(form.vehicles || []).reduce((s, v) => s + v.count, 0)}
                  lines={(form.vehicles || []).map((v) => `${v.count}× ${t(`veh.${v.category}.name`)}`)}
                  bookingFor={form.booking_for}
                  onBookingFor={(value) => setForm((current) => ({ ...current, booking_for: value }))}
                  onContinue={() => valid[1] && setStep(2)}
                />
              )}
            </div>
          </div>
        ) : (
          <AnimatePresence mode="wait"><motion.div key="done" {...stepMotion}>{confirmation}</motion.div></AnimatePresence>
        )}
      </main>
    </div>
  );
}
